import React, { useState, useEffect } from 'react';
import { APP_MESSAGES, DATE_FORMATS, REPORT_STATUS } from 'src/shared/config/src';
import { AxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import ProfileLayout from 'src/app/layouts/ProfileLayout';
import { useAppSelector } from 'src/app/services/store';
import { getCurrentChild } from 'src/shared/redux-state/src';
import { getSummativeReportAPI, signSummativeReport } from 'src/shared/services/src';
import { ChildrenStoreData, SummativeReportResponseType } from 'src/shared/types/src';
import { dateFormatter } from 'src/shared/utils/src';
import LegendsELG from './components/LegendsELG';
import COEL from './components/COEL';
import ReportFields from './components/ReportFields';
import { BaseCard, CustomButton, CustomCheckbox, CustomTextInput, SummativeReportLoader } from 'src/app/components';
import { toast } from 'react-toastify';
import { IoCaretDownSharp, IoCaretForwardSharp } from "react-icons/io5";
import { MdCheck } from "react-icons/md";
import { navigateToDownloadAppScreen } from 'src/app/utilities/helpers';
import TextRenderer from 'src/app/components/commonComponents/TextRenderer';

export const SummativeReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reportID } = location?.state as Readonly<{ reportID: string }>;

  const [isVisible, setIsVisible] = useState<Array<string>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportDetails, setReportDetails] = useState<SummativeReportResponseType>();
  const [acknowledged, setIsAcknowledged] = useState<boolean>(false);
  const [keepCopy, setKeepCopy] = useState<boolean>(false);
  const [shareWithOther, setShareWithOther] = useState<boolean>(false);
  const [parentComments, setParentComments] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

  useEffect(() => {
    init(reportID)
  }, [reportID]);

  const init = async (reportID: string) => {
    try {
      setLoading(true);

      const response = await getSummativeReportAPI({
        child: currentChild,
        reportID: reportID
      }) as SummativeReportResponseType

      if (response && Object.keys(response).length > 0) {
        setReportDetails(response)
      } else {
        setReportDetails({} as SummativeReportResponseType);
      }

    } catch (error) {
      if (error instanceof AxiosError) {
        const { errors } = error?.response?.data;
        if (errors && Object.keys(errors).length > 0) {
          toast.error(errors.join(", "), { autoClose: 5000 });
        } else {
          toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
      } else {
        toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
      }
    } finally {
      setLoading(false)
    }
  }

  const onPressComplete = async () => {
    if (!acknowledged) {
      return toast.error('Please tick the acknowledgement checkbox', { autoClose: 5000 });
    };

    setSubmitting(true);

    try {
      const response = await signSummativeReport({
        child: currentChild,
        reportID: reportID,
        params: {
          comment: parentComments,
          keepCopy: keepCopy,
          shareWithOther: shareWithOther
        }
      });

      if (response) {
        toast.success('Report submitted successfully', { autoClose: 5000 });

        setTimeout(() => {
          navigate(-1);
        }, 100);
      } else {
        toast.error('Could not submit report. Please try again after some time.', { autoClose: 5000 });
      }

    } catch (error) {
      if (error instanceof AxiosError) {
        const { errors } = error.response?.data;
        if (errors && Object.keys(errors).length > 0) {
          toast.error(JSON.stringify(errors), { autoClose: 5000 });
        } else {
          toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
      } else {
        toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const renderEYFSFrameworks = (el: any, index: number) => {
    return (
      <div className={`pd-y-15 mx-n-20 pd-x-20 ${index === 0 ? '' : 'border-top border-gray border-t-w-6'}`}>
        <div className='cursor-pointer' onClick={() => {
          if (isVisible.includes(el?.name)) {
            const filteredVisible = isVisible.filter((item) => item !== el?.name);
            setIsVisible(filteredVisible)
          } else {
            setIsVisible([
              ...isVisible,
              el?.name
            ])
          }
        }}>
          <div className="d-flex align-items-center cursor-pointer">
            <div className='visible-in-print-view'><IoCaretDownSharp size={15} /></div>
            <div className='print-element-hide'>{isVisible.includes(el?.name) ? <IoCaretDownSharp size={15} /> : <IoCaretForwardSharp size={15} />}</div>
            <div className='fs-14 fw-semibold text-black mg-l-10 flex-1'>
              {el?.name}
            </div>
          </div>
        </div>

        <div className='visible-in-print-view'>
          <div className="pd-l-25">
            {el?.currentProgress?.visible && (
              <div className='mg-t-15'>
                <div className='fs-12 fw-semibold text-black'>Current Progress</div>
                <div className='fs-12 text-black'>{el?.currentProgress?.value || ''}</div>
              </div>
            )}

            {el?.nextSteps?.visible && (
              <div className='mg-t-15'>
                <div className='fs-12 fw-semibold text-black'>Next Steps</div>
                <div className='fs-12 text-black'>{el?.nextSteps?.value || ''}</div>
              </div>
            )}

            {el?.aspects.map((frameAspect: any) => {
              return (
                <div className='mg-t-20'>
                  <p className='fs-12 fw-semibold text-black mg-b-0'>{frameAspect?.name || ''}</p>
                  <>
                    {frameAspect?.ageBand.map((frameAgeBand: any, index: number) => {
                      return (
                        <div className={`pd-l-10 ${index === 0 ? 'mg-t-10' : 'mg-t-15'}`}>
                          <div className="rounded-2 pd-y-6 pd-r-8 position-relative pd-l-22" style={{ border: '1px solid ' + (frameAgeBand?.color ? frameAgeBand?.color : '#E9E9E9') }}>
                            <div className="dots rounded position-absolute" style={{ backgroundColor: frameAgeBand?.color }}></div>
                            <p className="fs-12 text-black mg-b-0 fw-semibold">{frameAgeBand?.name || ''}</p>
                          </div>

                          {frameAgeBand?.statements.map((statements: any) => {
                            return (
                              <div className='d-flex align-items-center mg-t-10 mg-l-20'>
                                <div className='fs-12 text-black opacity-78 mg-b-0 flex-1'><TextRenderer text={statements?.name || ''} /></div>
                                {statements?.count ? <div className='bg-gray rounded-circle count d-inline-flex align-items-center justify-content-center mg-l-10'>
                                  <p className='fs-12 text-black mg-b-0 fw-semibold'>{statements?.count || ''}</p>
                                </div> : null}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </>
                </div>
              )
            })}

          </div>
        </div>
        <div className='print-element-hide'>{isVisible.includes(el?.name) && (
          <div className="pd-l-25">
            {el?.currentProgress?.visible && (
              <div className='mg-t-15'>
                <div className='fs-12 fw-semibold text-black'>Current Progress</div>
                <div className='fs-12 text-black'>{el?.currentProgress?.value || ''}</div>
              </div>
            )}

            {el?.nextSteps?.visible && (
              <div className='mg-t-15'>
                <div className='fs-12 fw-semibold text-black'>Next Steps</div>
                <div className='fs-12 text-black'>{el?.nextSteps?.value || ''}</div>
              </div>
            )}

            {el?.aspects.map((frameAspect: any) => {
              return (
                <div className='mg-t-20'>
                  <p className='fs-12 fw-semibold text-black mg-b-0'>{frameAspect?.name || ''}</p>
                  <>
                    {frameAspect?.ageBand.map((frameAgeBand: any, index: number) => {
                      return (
                        <div className={`pd-l-10 ${index === 0 ? 'mg-t-10' : 'mg-t-15'}`}>
                          <div className="rounded-2 pd-y-6 pd-r-8 position-relative pd-l-22" style={{ border: '1px solid ' + (frameAgeBand?.color ? frameAgeBand?.color : '#E9E9E9') }}>
                            <div className="dots rounded position-absolute" style={{ backgroundColor: frameAgeBand?.color }}></div>
                            <p className="fs-12 text-black mg-b-0 fw-semibold">{frameAgeBand?.name || ''}</p>
                          </div>

                          {frameAgeBand?.statements.map((statements: any) => {
                            return (
                              <div className='d-flex align-items-center mg-t-10 mg-l-20'>
                                <div className='fs-12 text-black opacity-78 mg-b-0 flex-1'><TextRenderer text={statements?.name || ''} /></div>
                                {statements?.count ? <div className='bg-gray rounded-circle count d-inline-flex align-items-center justify-content-center mg-l-10'>
                                  <p className='fs-12 text-black mg-b-0 fw-semibold'>{statements?.count || ''}</p>
                                </div> : null}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </>
                </div>
              )
            })}

          </div>
        )}</div>
      </div>
    )
  }

  if (loading) {
    return <SummativeReportLoader />
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <ProfileLayout title={"Summative Report"} fixedFooter={reportDetails?.status === REPORT_STATUS.PENDING_PARENT_REVIEW ? true : false} isPrintPage={true} handlePrint={() => handlePrint()}>
      {/* {submitting && <div>Loading...</div>} */}
      <div className="fs-12 text-gray-300 text-center">
        <span className="fw-bold">{reportDetails?.name}</span> - {reportDetails?.date ? dateFormatter(reportDetails?.date, DATE_FORMATS.SHORT_DATE) : ''}
      </div>
      <div className="fs-12 text-gray-300 text-center mg-t-5">
        {reportDetails?.child?.age} Months {reportDetails?.child.roomName ? `- ${reportDetails?.child.roomName}` : null}
      </div>
      {reportDetails && reportDetails?.legends?.eyfs?.length > 0 ?
        <div className="mg-t-20">
          <LegendsELG
            data={reportDetails?.legends?.eyfs}
            title="Legend"
          />
        </div> : null
      }
      {reportDetails && reportDetails?.frameworks?.eyfs.areas?.length > 0 ?
        reportDetails?.frameworks?.eyfs.areas.map((el, index) => {
          return (
            <div>
              {renderEYFSFrameworks(el, index)}
            </div>
          )
        }) : null
      }

      {reportDetails && (reportDetails?.frameworks?.coel?.areas?.length > 0 || reportDetails?.legends?.elg?.length > 0) ?
        <div className={`pd-y-15 mx-n-20 pd-x-20 border-top border-gray border-t-w-6`}>

          {reportDetails?.legends?.elg?.length > 0 ?
            <div className="mg-t-15 mg-b-15">
              <LegendsELG
                data={reportDetails?.legends?.elg}
                title="Legend (Early Learning Goals)"
              /></div> : null
          }

          {reportDetails?.frameworks?.coel?.areas?.length > 0 ? <COEL
            data={reportDetails?.frameworks?.coel?.areas}
            currTabDetailsShown={isVisible}
            onPressTab={(isVisibleArray: Array<string>) => setIsVisible(isVisibleArray)}
            title="Characteristics of Effective Learning:"
          /> : null}

        </div>
        : null}

      {reportDetails && reportDetails?.fields?.length > 0 ?
        <ReportFields
          data={reportDetails?.fields}
        /> : null
      }

      <div className={`mx-n-20 pd-x-20 border-top border-gray border-t-w-6`}>
        <div className='border border-gray-70 rounded-3 pd-15 mg-t-15'>
          <div className='form-group'>
            <div className='form-label text-gray-100 mb-1 fs-12'>Staff Name</div>
            <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.staff?.value || ''}</div>
          </div>

          <div className='form-group mg-t-15'>
            <div className='form-label text-gray-100 mb-1 fs-12'>Date</div>
            <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.staff?.timestamp ? dateFormatter(reportDetails?.signatures?.staff?.timestamp, DATE_FORMATS.SHORT_DATE) : ''}</div>
          </div>
        </div>
        {reportDetails?.parentComments?.visible && (
          <>
            {REPORT_STATUS.PENDING_PARENT_REVIEW !== reportDetails?.status && <div className='form-group mg-t-15'>
              <div className='form-label text-black fw-semibold mb-1 fs-12'>Parent(s) Comments</div>

              <BaseCard customBaseCardStyle="min-h100 mg-t-10 rounded-2 pd-15"><p className="mg-b-0 form-control read-only-control d-flex align-items-center">{reportDetails?.parentComments?.value || ''}</p></BaseCard>

              {/* {REPORT_STATUS.PENDING_PARENT_REVIEW === reportDetails?.status && (
                    <CustomTextInput
                      name={parentComments}
                      value={parentComments}
                      onChange={(e) => { setParentComments(e.target.value) }}
                      placeholder='Type here'
                      type="text"
                    />
                  )} */}
            </div>}
            <div className='mg-t-20'>
              {(REPORT_STATUS.PENDING_PARENT_REVIEW !== reportDetails?.status) && (reportDetails?.parentComments?.permissions.length > 0) && (
                reportDetails?.parentComments?.permissions.map((el, index) => {
                  return (
                    <div className={`${index === 0 ? '' : 'mg-t-15'}`}>
                      <div className="d-flex align-items-center">
                        {el?.value ? <div className='checked-circle mg-r-5 rounded-circle bg-gray-200 d-flex align-items-center justify-content-center'><MdCheck size={12} /></div> : <div className='checked-circle mg-r-5 rounded-circle border border-gray-900'></div>}
                        <p className="fs-12 text-black mg-b-0 mg-l-5">{el?.label || ''}</p>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            {/* {(REPORT_STATUS.PENDING_PARENT_REVIEW === reportDetails?.status) && (reportDetails?.parentComments?.permissions.length > 0) && (
                  <>
                    <div>
                      <CustomCheckbox
                        isChecked={keepCopy}
                        onChange={() => setKeepCopy(val => !val)}
                        label={reportDetails?.parentComments?.permissions[0]?.label || ''}
                      />
                    </div>

                    <div>
                      <CustomCheckbox
                        isChecked={shareWithOther}
                        onChange={() => setShareWithOther(val => !val) }
                        label={reportDetails?.parentComments?.permissions[1]?.label || ''}
                      />
                    </div>
                  </>
                )} */}
          </>
        )}

        {reportDetails?.status !== REPORT_STATUS.PENDING_PARENT_REVIEW ? <div className='form-group mg-t-15'>
          <div className='form-label text-black mb-1 fs-12'>Parent(s) / Carer(s) Name</div>
          <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.parent?.value || ''}</div>
        </div> : null}

        {reportDetails?.status !== REPORT_STATUS.PENDING_PARENT_REVIEW ? <div className='form-group mg-t-15'>
          <div className='form-label text-black mb-1 fs-12'>Date</div>
          <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.parent?.timestamp ? dateFormatter(reportDetails?.signatures?.parent?.timestamp, DATE_FORMATS.LONG_DATE_WITH_TIME) : ''}</div>
        </div> : null}

        {reportDetails?.status === REPORT_STATUS.PENDING_PARENT_REVIEW ?
          <>
            {/* <div className='form-group mg-t-15'>
                  <div className='form-label text-black mb-1 fs-12'>Parent Acknowledgement:</div>
                  <div>
                    <CustomCheckbox
                      isChecked={acknowledged}
                      onChange={() => setIsAcknowledged(val => !val) }
                      label={"I have read the report and entered my comments as required."}
                    />
                  </div>
                </div> */}

            <div className="section-fixed pd-b-10 pd-t-10 pd-x-20 print-element-hide">
              <CustomButton
                text='Submit to Complete'
                // onClick={onPressComplete}
                onClick={navigateToDownloadAppScreen}
                color={'primary'}
                isLoading={submitting}
              />
            </div>
          </> : null
        }
      </div>
    </ProfileLayout>
  )
}

export default SummativeReport;